import { useEffect, useRef, useState } from "react"
import { isAbortError } from "~/common/utils"

type PlaybackOptions = {
  startTime?: number
  endTime?: number
  loop?: boolean
}

export const usePlayback = ({ startTime, endTime, loop }: PlaybackOptions) => {
  const ref = useRef<HTMLVideoElement | null>(null)
  const previousRange = useRef({ startTime, endTime })
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(startTime ?? 0)
  const [aspectRatio, setAspectRatio] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [ended, setEnded] = useState(false)
  const [failed, setFailed] = useState(false)
  const [playFailed, setPlayFailed] = useState(false)
  const [volume, setVolume] = useState(1)
  const [muted, setMuted] = useState(false)
  const start = Math.max(0, startTime ?? 0)
  const end = Math.max(start, Math.min(endTime ?? duration, duration))
  const length = Math.max(0, end - start)
  const elapsed = Math.min(length, Math.max(0, currentTime - start))
  const invalidRange = duration > 0 && length <= 0

  const play = () => {
    const video = ref.current
    if (!video || failed || invalidRange) return
    setPlayFailed(false)
    void video.play().catch((error: unknown) => {
      if (ref.current !== video) return
      // Pausing, seeking or replacing the source may interrupt a pending play.
      if (isAbortError(error)) return
      setPlaying(false)
      setPlayFailed(true)
    })
  }

  const toggle = () => {
    const video = ref.current
    if (!video || failed || invalidRange) return
    if (!video.paused) {
      video.pause()
      return
    }
    if (
      ended ||
      video.currentTime < start ||
      (end > start && video.currentTime >= end)
    ) {
      video.currentTime = start
      setCurrentTime(start)
    }
    play()
  }

  const finish = () => {
    const video = ref.current
    if (!video) return
    if (!loop) video.pause()
    video.currentTime = start
    setCurrentTime(start)
    if (loop) {
      play()
    } else {
      setPlaying(false)
      setEnded(true)
    }
  }

  const syncTime = () => {
    const video = ref.current
    if (!video) return
    if (!video.paused && end > start && video.currentTime >= end) {
      finish()
      return
    }
    setCurrentTime(video.currentTime)
  }

  const syncVolume = () => {
    const video = ref.current
    if (!video) return
    setVolume(video.volume)
    setMuted(video.muted)
  }

  const loadMetadata = () => {
    const video = ref.current
    if (!video) return
    setDuration(Number.isFinite(video.duration) ? video.duration : 0)
    setAspectRatio(
      video.videoHeight > 0 ? video.videoWidth / video.videoHeight : 0,
    )
    if (startTime !== undefined && start < video.duration)
      video.currentTime = start
    setCurrentTime(video.currentTime)
    syncVolume()
  }

  const handlePlay = () => {
    const video = ref.current
    if (!video) return
    if (invalidRange) {
      video.pause()
      return
    }
    if (
      video.currentTime < start ||
      (end > start && video.currentTime >= end)
    ) {
      video.currentTime = start
    }
    setEnded(false)
    setPlayFailed(false)
    setPlaying(true)
  }

  const seek = (value: number) => {
    const video = ref.current
    if (!video || video.readyState < 1) return
    const time = Math.min(end, start + Math.max(0, value))
    setEnded(false)
    video.currentTime = time
    setCurrentTime(time)
    if (time >= end) video.pause()
  }

  const changeVolume = (value: number) => {
    const video = ref.current
    if (!video) return
    video.volume = Math.min(1, Math.max(0, value))
    video.muted = video.volume === 0
    syncVolume()
  }

  useEffect(() => {
    const previous = previousRange.current
    previousRange.current = { startTime, endTime }
    if (previous.startTime === startTime && previous.endTime === endTime) return
    const video = ref.current
    if (!video) return
    video.pause()
    if (video.readyState >= 1 && start < video.duration)
      video.currentTime = start
    setCurrentTime(start)
    setEnded(false)
    setPlaying(false)
  }, [start, startTime, endTime])

  return {
    ref,
    aspectRatio,
    playing,
    ended,
    failed,
    playFailed,
    volume,
    muted,
    length,
    elapsed,
    invalidRange,
    toggle,
    finish,
    syncTime,
    syncVolume,
    loadMetadata,
    handlePlay,
    seek,
    changeVolume,
    handlePause: () => setPlaying(false),
    handleError: () => {
      setFailed(true)
      setPlaying(false)
    },
    handleDurationChange: () => {
      const value = ref.current?.duration
      setDuration(value !== undefined && Number.isFinite(value) ? value : 0)
    },
    handleSeek: () => {
      if (ref.current && ref.current.currentTime !== start) setEnded(false)
    },
  }
}
